import type { MembershipContractTimeline } from '@libs/data-access/entities'
import { Alert, Button, Flex, Title } from '@libs/ui/ds'
import { useMemberPageTranslation } from '@libs/utils/translations'
import moment from 'moment'
import React, { useEffect } from 'react'
import type { FC } from 'react'
import { useFormContext } from 'react-hook-form'

import { MoveToNewContract } from './components'

type Props = {
  username: string
  contract: MembershipContractTimeline
  formContractStart: string
  formContractEnd: string
  isStartDateUpdated?: boolean
  isCreatingNewContract: boolean
  onDeleteShiftsSubmit: () => void
  setIsCreatingNewContract: (display: boolean) => void
  onMoveToNewContractSubmit: () => void
  isLoadingPlannables: boolean
}
const MoveShiftsToNewContractContainer: FC<Props> = ({
  username,
  contract,
  formContractStart,
  formContractEnd,
  isStartDateUpdated = false,
  isCreatingNewContract,
  onDeleteShiftsSubmit,
  setIsCreatingNewContract,
  onMoveToNewContractSubmit,
  isLoadingPlannables
}) => {
  const { t } = useMemberPageTranslation()
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext()

  const description = isStartDateUpdated
    ? t('contract.form.movingBeforeDate', {
        user: username,
        date: formContractStart
      })
    : t('contract.form.movingAfterDate', {
        user: username,
        date: formContractEnd
      })

  useEffect(() => {
    if (isCreatingNewContract && isStartDateUpdated) {
      setValue('new_contract_type', contract?.contract_type)
      setValue(
        'new_contract_end',
        moment(formContractStart).subtract(1, 'day').format('YYYY-MM-DD')
      )
      setValue(
        'new_contract_start',
        moment(contract?.contract_start).add(1, 'day').format('YYYY-MM-DD')
      )
    } else {
      setValue(
        'new_contract_start',
        moment(formContractEnd).add(1, 'day').format('YYYY-MM-DD')
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartDateUpdated, isCreatingNewContract])

  return (
    <div>
      <Alert intent='semantic.orange' title={t('contract.form.warm')}>
        {description}
      </Alert>
      {isCreatingNewContract && (
        <MoveToNewContract
          isLoadingPlannables={isLoadingPlannables}
          control={control}
          errors={errors}
          onMoveToNewContractSubmit={onMoveToNewContractSubmit}
          setIsCreatingNewContract={setIsCreatingNewContract}
        />
      )}
      {!isCreatingNewContract && (
        <Flex className='mt-2 gap-2'>
          <Title size={4}>{t('contract.form.options')}</Title>
          <ul>
            <li>
              {isStartDateUpdated
                ? t('contract.form.saveStartDate')
                : t('contract.form.closeStartDate')}
            </li>
            <li>
              {isStartDateUpdated
                ? t('contract.form.saveNewStartDate')
                : t('contract.form.closeNewStartDate')}
            </li>
          </ul>
          <Flex className='gap-2'>
            <Button
              intent='danger'
              onClick={onDeleteShiftsSubmit}
              disabled={isLoadingPlannables}
            >
              {isStartDateUpdated
                ? t('contract.form.saveRemoveShift')
                : t('contract.form.closeRemoveShift')}
            </Button>
            <Button
              intent='danger'
              onClick={() => setIsCreatingNewContract(true)}
              disabled={isLoadingPlannables}
            >
              {isStartDateUpdated
                ? t('contract.form.saveNewContract')
                : t('contract.form.closeNewContract')}
            </Button>
          </Flex>
        </Flex>
      )}
    </div>
  )
}

export default MoveShiftsToNewContractContainer
